import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { User, UserManager } from 'oidc-client-ts';
import { baseURL } from 'app/client';
import { GenericLoading } from 'components/ui/Loader';

interface TokenValidationResponse {
  success: boolean;
  accessToken?: string;
  tokenType?: string;
  expiresIn?: number;
  refreshToken?: string;
  idToken?: string;
  userInfo?: {
    sub: string;
    name?: string;
    email?: string;
    preferredUsername?: string;
    givenName?: string;
    familyName?: string;
    roles: string[];
  };
  error?: string;
}

const SsoCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const token = searchParams.get('token');

  useEffect(() => {
    const handleSsoLogin = async () => {
      if (!token) {
        setError('Token manquant dans l\'URL');
        setIsProcessing(false);
        return;
      }

      try {
        const response = await fetch(
          `${baseURL}/api/backoffice/authentification/login?token=${token}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }

        const result: TokenValidationResponse = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Échec de l\'authentification');
        }

        if (!result.accessToken) {
          throw new Error('Token d\'accès manquant dans la réponse');
        }

        const user = new User({
          access_token: result.accessToken,
          token_type: result.tokenType || 'Bearer',
          refresh_token: result.refreshToken,
          id_token: result.idToken,
          scope: 'openid profile email BackOffice offline_access',
          profile: {
            sub: result.userInfo?.sub || '',
            name: result.userInfo?.name || '',
            email: result.userInfo?.email || '',
            preferred_username: result.userInfo?.preferredUsername || '',
            given_name: result.userInfo?.givenName || '',
            family_name: result.userInfo?.familyName || '',
            role: result.userInfo?.roles || []
          } as any,
          expires_at: Math.floor(Date.now() / 1000) + (result.expiresIn || 3600), // <-- expires_at, pas expires_in
          session_state: null
        });

        const userManager = new UserManager(auth.settings);
        await userManager.storeUser(user);
        await auth.signinSilent();

        // 5. Naviguer vers le dashboard
        navigate('/bord', { replace: true });

      } catch (error) {
        console.error('Erreur lors de l\'authentification SSO:', error);
        const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
        setError(errorMessage);
        setIsProcessing(false);
      }
    };
    handleSsoLogin();
  }, [token, navigate, auth.settings.userStore]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Erreur de connexion
            </h2>
            <p className="mt-2 text-sm text-gray-600">{error}</p>
          </div>
          <button
            onClick={() => window.location.href = 'votre-url-sso'}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return <GenericLoading loading={isProcessing} isFullPage />;
};

export default SsoCallback;
